import { useId, useState, type ReactNode } from "react";
import { cx } from "../../cx";
import { HelpPopover } from "../HelpPopover/HelpPopover";

// UXR-42: four standard page templates. Pure layout with slots (no i18n, no app imports) so the
// dashboard passes translated text plus its own PageHeader extras, EmptyState and ApiErrorState.
// Layout uses logical utilities only (start/end, ps/pe, gap), so it mirrors in RTL.

export interface PageFrameProps {
  /** Page title: always the first thing at the start edge. */
  readonly title: string;
  readonly description?: string;
  /** Small inline element beside the title (e.g. a help link). */
  readonly titleExtra?: ReactNode;
  /** The ONE primary action. Always at the end edge of the header row, on every template. */
  readonly primaryAction?: ReactNode;
  /** Collapsible explainer. Collapsed by default; opens as an overlay, never pushing content. */
  readonly explainerNode?: ReactNode;
  readonly explainer?: {
    readonly label: string;
    readonly content: ReactNode;
    readonly defaultOpen?: boolean | undefined;
  };
  /** Breadcrumb / back link above the title (DetailPage, WizardPage). */
  readonly backLink?: ReactNode;
  readonly children?: ReactNode;
  readonly testId?: string;
}

/** Collapsible explainer card. The panel floats over the page so toggling never shifts layout. */
export function ExplainerCard({
  label,
  children,
  defaultOpen = false,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly defaultOpen?: boolean | undefined;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <div className="relative" data-explainer data-state={open ? "open" : "closed"}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          "inline-flex min-h-8 items-center gap-2 rounded-xl border border-line bg-surface px-3 py-1",
          "text-sm font-medium text-on-action-ghost hover:bg-action-ghost-hover",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
        )}
      >
        {label}
      </button>
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-label={label}
          className="absolute start-0 top-full z-20 mt-2 w-[min(32rem,90vw)] rounded-2xl border border-line bg-surface p-5 text-sm text-fg shadow-lg"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function PageFrameHeader({
  title,
  description,
  titleExtra,
  primaryAction,
  explainer,
  explainerNode,
  backLink,
}: Omit<PageFrameProps, "children" | "testId">) {
  return (
    <header className="flex flex-col gap-2" data-page-header>
      {backLink ? <div className="text-sm">{backLink}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-2xl font-bold text-fg">{title}</h1>
            {titleExtra}
            {description ? <HelpPopover>{description}</HelpPopover> : null}
            {explainer ? (
              <ExplainerCard label={explainer.label} defaultOpen={explainer.defaultOpen}>
                {explainer.content}
              </ExplainerCard>
            ) : null}
            {explainerNode ? (
              <div
                data-explainer-slot
                className={cx(
                  "relative",
                  "[&_[data-feature-intro][data-state=expanded]]:absolute [&_[data-feature-intro][data-state=expanded]]:start-0",
                  "[&_[data-feature-intro][data-state=expanded]]:top-full [&_[data-feature-intro][data-state=expanded]]:z-20",
                  "[&_[data-feature-intro][data-state=expanded]]:mt-2 [&_[data-feature-intro][data-state=expanded]]:w-[min(32rem,90vw)]",
                  "[&_[data-feature-intro][data-state=expanded]]:bg-surface [&_[data-feature-intro][data-state=expanded]]:shadow-lg",
                )}
              >
                {explainerNode}
              </div>
            ) : null}
          </div>
          {description ? <p className="sr-only">{description}</p> : null}
        </div>
        {primaryAction ? (
          <div className="flex shrink-0 items-center" data-primary-action>
            {primaryAction}
          </div>
        ) : null}
      </div>
    </header>
  );
}

function Frame({
  testId,
  template,
  children,
  ...header
}: PageFrameProps & { readonly template: string }) {
  return (
    <section
      aria-label={header.title}
      data-page-template={template}
      data-testid={testId}
      className="flex flex-col gap-6"
    >
      <PageFrameHeader {...header} />
      {children}
    </section>
  );
}

/** Filters / toolbar row: wraps, with real gaps, secondary controls only. */
export function PageToolbar({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label?: string | undefined;
}) {
  return (
    <div
      role="toolbar"
      aria-label={label}
      data-page-toolbar
      className="flex flex-wrap items-center gap-x-4 gap-y-3"
    >
      {children}
    </div>
  );
}

/** Shared body: error beats loading beats empty beats content. */
function Body({
  error,
  loading,
  empty,
  children,
}: {
  readonly error?: ReactNode;
  readonly loading?: ReactNode;
  readonly empty?: ReactNode;
  readonly children?: ReactNode;
}) {
  if (error) return <div data-page-state="error">{error}</div>;
  if (loading) return <div data-page-state="loading">{loading}</div>;
  if (empty) return <div data-page-state="empty">{empty}</div>;
  return <>{children}</>;
}

export interface ListPageProps extends PageFrameProps {
  /** Filters row between header and list. */
  readonly filters?: ReactNode;
  /** Secondary actions (view toggle, export) at the end of the filters row. */
  readonly toolbarActions?: ReactNode;
  readonly toolbarLabel?: string;
  readonly error?: ReactNode;
  readonly loading?: ReactNode;
  /** Rendered instead of the list when set (e.g. `<EmptyState>`). */
  readonly empty?: ReactNode;
}

export function ListPage({
  filters,
  toolbarActions,
  toolbarLabel,
  error,
  loading,
  empty,
  children,
  ...frame
}: ListPageProps) {
  return (
    <Frame template="list" {...frame}>
      {filters || toolbarActions ? (
        <PageToolbar label={toolbarLabel}>
          {filters}
          {toolbarActions ? (
            <div className="ms-auto flex items-center gap-3">{toolbarActions}</div>
          ) : null}
        </PageToolbar>
      ) : null}
      <Body error={error} loading={loading} empty={empty}>
        {children}
      </Body>
    </Frame>
  );
}

export interface DetailPageProps extends PageFrameProps {
  /** Side column (summary, status, history); stacks below on narrow screens. */
  readonly aside?: ReactNode;
  readonly error?: ReactNode;
  readonly loading?: ReactNode;
}

export function DetailPage({ aside, error, loading, children, ...frame }: DetailPageProps) {
  return (
    <Frame template="detail" {...frame}>
      <Body error={error} loading={loading}>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="flex min-w-0 flex-col gap-6">{children}</div>
          {aside ? <aside className="flex min-w-0 flex-col gap-4">{aside}</aside> : null}
        </div>
      </Body>
    </Frame>
  );
}

export interface WizardPageProps extends PageFrameProps {
  /** Already-translated step labels. */
  readonly steps: readonly string[];
  /** Zero-based current step. */
  readonly currentStep: number;
  /** Step progress text, e.g. "Step 2 of 4". */
  readonly progressLabel: string;
  /** Back / secondary buttons, start side of the footer. */
  readonly footerStart?: ReactNode;
  readonly error?: ReactNode;
}

export function WizardPage({
  steps,
  currentStep,
  progressLabel,
  footerStart,
  error,
  children,
  primaryAction,
  ...frame
}: WizardPageProps) {
  // The wizard's single primary action ("Next") sits in the header slot like every template.
  return (
    <Frame template="wizard" primaryAction={primaryAction} {...frame}>
      <nav aria-label={progressLabel} data-wizard-steps>
        <p className="text-sm text-fg-muted">{progressLabel}</p>
        <ol className="m-0 flex list-none flex-wrap gap-x-4 gap-y-2 p-0">
          {steps.map((s, i) => (
            <li
              key={s}
              aria-current={i === currentStep ? "step" : undefined}
              data-done={i < currentStep}
              className={cx(
                "text-sm",
                i === currentStep ? "font-semibold text-fg" : "text-fg-muted",
              )}
            >
              {s}
            </li>
          ))}
        </ol>
      </nav>
      <Body error={error}>
        <div className="flex max-w-2xl flex-col gap-4">{children}</div>
      </Body>
      {footerStart ? <footer className="flex items-center gap-3">{footerStart}</footer> : null}
    </Frame>
  );
}

export interface SettingsSection {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly content: ReactNode;
}

export interface SettingsPageProps extends PageFrameProps {
  readonly sections: readonly SettingsSection[];
  readonly error?: ReactNode;
}

export function SettingsPage({ sections, error, children, ...frame }: SettingsPageProps) {
  return (
    <Frame template="settings" {...frame}>
      <Body error={error}>
        <div className="flex max-w-3xl flex-col gap-6">
          {sections.map((s) => (
            <section
              key={s.id}
              aria-labelledby={`settings-${s.id}`}
              className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-6"
            >
              <div className="flex flex-col gap-1">
                <h2 id={`settings-${s.id}`} className="text-base font-semibold text-fg">
                  {s.title}
                </h2>
                {s.description ? <p className="text-sm text-fg-muted">{s.description}</p> : null}
              </div>
              {s.content}
            </section>
          ))}
          {children}
        </div>
      </Body>
    </Frame>
  );
}
